import { LegalCmsContent } from '../models/types';

export const PRIVACY_CMS_IDENTIFIER = 'privacy-policy';
export const TERMS_CMS_IDENTIFIER = 'terms-and-conditions';

export const DEFAULT_PRIVACY_CMS: LegalCmsContent = {
  title: 'Privacy Policy',
  subtitle: 'How DEX collects, uses, and protects your information.',
  lastUpdated: 'June 10, 2026',
  intro:
    'DEX is committed to protecting your privacy. This Privacy Policy explains what information we collect when you use our decentralized exchange platform, how we use it, and the choices you have regarding your data.',
  sections: [
    {
      heading: 'Information We Collect',
      body:
        'We may collect wallet addresses, transaction data submitted on-chain, usage analytics, and information you voluntarily provide through contact forms or support requests. We do not collect traditional account passwords because DEX is a non-custodial platform.',
    },
    {
      heading: 'How We Use Information',
      body:
        'Information is used to operate and improve the platform, provide customer support, prevent fraud and abuse, comply with legal obligations, and communicate important service updates. On-chain activity is public by nature and governed by blockchain protocols.',
    },
    {
      heading: 'Cookies & Analytics',
      body:
        'We may use cookies and similar technologies to remember preferences, measure site performance, and improve user experience. You can control cookie settings through your browser.',
    },
    {
      heading: 'Data Sharing',
      body:
        'We do not sell your personal information. We may share limited data with infrastructure providers, analytics partners, or authorities when required by law or to protect the security of our users and platform.',
    },
    {
      heading: 'Your Rights',
      body:
        'Depending on your jurisdiction, you may have rights to access, correct, or delete personal data we hold. Contact us to submit a privacy-related request.',
    },
    {
      heading: 'Contact',
      body:
        'For privacy questions or requests, please use the Contact page on our website or reach out through our official community channels.',
    },
  ],
  seo: {
    metaTitle: 'Privacy Policy | DEX',
    metaDescription:
      'Read the DEX Privacy Policy to understand how we handle wallet, usage, and contact information.',
    keywords: 'dex, privacy policy, data protection, defi privacy',
    ogImage: '',
  },
};

export const DEFAULT_TERMS_CMS: LegalCmsContent = {
  title: 'Terms & Conditions',
  subtitle: 'The rules and guidelines for using the DEX platform.',
  lastUpdated: 'June 10, 2026',
  intro:
    'By accessing or using DEX, you agree to these Terms & Conditions. Please read them carefully. If you do not agree, you should not use the platform.',
  sections: [
    {
      heading: 'Eligibility',
      body:
        'You must be at least 18 years old and legally permitted to use decentralized finance services in your jurisdiction. You are responsible for ensuring compliance with local laws.',
    },
    {
      heading: 'Non-Custodial Service',
      body:
        'DEX is a non-custodial decentralized exchange interface. You retain control of your wallets and private keys. We never take custody of your assets.',
    },
    {
      heading: 'Trading & Financial Risk',
      body:
        'Digital assets are volatile and involve substantial risk. You are solely responsible for your trading decisions, slippage, impermanent loss, smart contract risks, and any losses incurred.',
    },
    {
      heading: 'Prohibited Use',
      body:
        'You may not use DEX for unlawful activity, market manipulation, money laundering, sanctions evasion, or any activity that harms the protocol or other users.',
    },
    {
      heading: 'Intellectual Property',
      body:
        'DEX branding, interface design, and content are protected by applicable intellectual property laws. You may not copy or redistribute platform materials without permission.',
    },
    {
      heading: 'Limitation of Liability',
      body:
        'DEX is provided on an "as is" basis without warranties. To the fullest extent permitted by law, DEX and its contributors are not liable for indirect, incidental, or consequential damages arising from platform use.',
    },
    {
      heading: 'Changes to Terms',
      body:
        'We may update these Terms from time to time. Continued use of the platform after updates constitutes acceptance of the revised Terms.',
    },
  ],
  seo: {
    metaTitle: 'Terms & Conditions | DEX',
    metaDescription:
      'Review the DEX Terms & Conditions governing use of our decentralized exchange platform.',
    keywords: 'dex, terms and conditions, terms of service, defi terms',
    ogImage: '',
  },
};

export const parseLegalCmsContent = (
  content?: string,
  fallback: LegalCmsContent = DEFAULT_PRIVACY_CMS
): LegalCmsContent => {
  if (!content) {
    return fallback;
  }

  try {
    const parsed = typeof content === 'string' ? JSON.parse(content) as Partial<LegalCmsContent> : content as Partial<LegalCmsContent>;
    return {
      ...fallback,
      ...parsed,
      seo: { ...fallback.seo, ...parsed.seo },
      sections: parsed.sections?.length ? parsed.sections : fallback.sections,
    };
  } catch {
    return fallback;
  }
};

export const LEGAL_CMS_MAP: Record<string, LegalCmsContent> = {
  [PRIVACY_CMS_IDENTIFIER]: DEFAULT_PRIVACY_CMS,
  [TERMS_CMS_IDENTIFIER]: DEFAULT_TERMS_CMS,
};
